import Shepherd from 'shepherd.js';

class TourService {
  constructor() {
    this.currentTour = null;
    this.tourCompletionStatus = this.loadTourStatus();
  }

  // Load tour completion status from localStorage
  loadTourStatus() {
    try {
      const status = localStorage.getItem('tour_completion_status');
      return status ? JSON.parse(status) : {};
    } catch {
      return {};
    }
  }

  // Save tour completion status
  saveTourStatus() {
    try {
      localStorage.setItem('tour_completion_status', JSON.stringify(this.tourCompletionStatus));
    } catch (error) {
      console.error('Failed to save tour status:', error);
    }
  }

  // Mark a tour as completed
  markTourCompleted(tourName) {
    this.tourCompletionStatus[tourName] = true;
    this.saveTourStatus();
  }

  // Check if tour is completed
  isTourCompleted(tourName) {
    return this.tourCompletionStatus[tourName] || false;
  }

  // Reset all tours (useful for testing)
  resetAllTours() {
    this.tourCompletionStatus = {};
    this.saveTourStatus();
  }

  // Default tour options
  getDefaultTourOptions() {
    return {
      defaultStepOptions: {
        cancelIcon: {
          enabled: true
        },
        classes: 'resume-tour-step shepherd-theme-arrows',
        scrollTo: { 
          behavior: 'smooth', 
          block: 'center' 
        },
        when: {
          show: () => {
            // Add backdrop
            document.body.classList.add('shepherd-active');
          },
          hide: () => {
            // Remove backdrop when step is hidden
            if (!this.currentTour?.isActive()) {
              document.body.classList.remove('shepherd-active');
            }
          }
        }
      },
      exitOnEsc: true,
      keyboardNavigation: true,
      useModalOverlay: true
    };
  }

  // Create common button configurations
  getButtonConfig() {
    return {
      back: {
        action() {
          return this.back();
        },
        classes: 'shepherd-button-secondary',
        text: 'Back'
      },
      next: {
        action() {
          try {
            return this.next();
          } catch (error) {
            console.error('Tour next error:', error);
            return false;
          }
        },
        text: 'Next'
      },
      skip: {
        action() {
          return this.cancel();
        },
        classes: 'shepherd-button-secondary',
        text: 'Skip Tour'
      },
      finish: {
        action() {
          this.complete();
          document.body.classList.remove('shepherd-active');
          return true;
        },
        text: 'Finish Tour',
        classes: 'shepherd-button-primary'
      },
      done: {
        action() {
          this.complete();
          document.body.classList.remove('shepherd-active');
          return true;
        },
        text: 'Done',
        classes: 'shepherd-button-primary'
      }
    };
  }

  // Main onboarding tour for new users
  createMainOnboardingTour(navigate, userStatus = {}) {
    const tour = new Shepherd.Tour(this.getDefaultTourOptions());
    const buttons = this.getButtonConfig();

    tour.addStep({
      title: '🎉 Welcome to Resume Journey!',
      text: `
        <div style="text-align: center; margin-bottom: 16px;">
          <div style="font-size: 64px; margin-bottom: 8px;">🚀</div>
          <div style="color: #667eea; font-weight: 600;">Your Personal Job Search Companion</div>
        </div>
        <p>Let me guide you through the 6 key steps to land your dream job:</p>
        <ol style="margin: 12px 0; padding-left: 20px; line-height: 1.6;">
          <li><strong>👤 Build Profile</strong> - Create your professional profile</li>
          <li><strong>🧭 Find Direction</strong> - Discover career paths for you</li>
          <li><strong>🎯 Hunt for Jobs</strong> - Search and save opportunities</li>
          <li><strong>✨ Polish Resume</strong> - Tailor resume for each job</li>
          <li><strong>📋 Track Applications</strong> - Monitor your progress</li>
          <li><strong>🎤 Practice Interviews</strong> - Prepare for success</li>
        </ol>
      `,
      buttons: [buttons.skip, buttons.next],
      id: 'welcome'
    });

    // Step 1: Profile
    tour.addStep({
      title: '👤 Step 1: Build Your Professional Profile',
      text: `
        <div style="text-align: center; margin-bottom: 12px;">
          <div style="font-size: 48px; margin-bottom: 8px;">👔</div>
        </div>
        <p>Start by creating a comprehensive professional profile that showcases:</p>
        <ul style="margin: 8px 0; padding-left: 20px;">
          <li>Your skills and experience</li>
          <li>Career preferences and goals</li>
          <li>Educational background</li>
          <li>Professional achievements</li>
        </ul>
        <div style="margin: 12px 0; padding: 8px; background: #f0f9ff; border-radius: 6px; border-left: 3px solid #0369a1;">
          <p style="margin: 0; font-weight: 500; color: #0369a1;">💡 Pro Tip: This forms the foundation for everything else!</p>
        </div>
      `,
      attachTo: {
        element: '#professional-profile-nav',
        on: 'bottom'
      },
      buttons: [buttons.back, buttons.skip, buttons.next],
      id: 'profile-step'
    });

    // Step 2: Career Direction
    tour.addStep({
      title: '🧭 Step 2: Find Your Career Direction',
      text: `
        <div style="text-align: center; margin-bottom: 12px;">
          <div style="font-size: 48px; margin-bottom: 8px;">🎯</div>
        </div>
        <p>Our AI analyzes your profile to recommend the best career paths:</p>
        <ul style="margin: 8px 0; padding-left: 20px;">
          <li>Personalized job role suggestions</li>
          <li>Skills gap analysis</li>
          <li>Career growth opportunities</li>
          <li>Market demand insights</li>
        </ul>
        <div style="margin: 12px 0; padding: 8px; background: #fef3c7; border-radius: 6px; border-left: 3px solid #d97706;">
          <p style="margin: 0; font-weight: 500; color: #92400e;">✨ Discover roles you might not have considered!</p>
        </div>
      `,
      attachTo: {
        element: '#resumes-nav',
        on: 'bottom'
      },
      buttons: [buttons.back, buttons.skip, buttons.next],
      id: 'direction-step'
    });

    // Step 3: Job Hunting
    tour.addStep({
      title: '🎯 Step 3: Hunt for Jobs',
      text: `
        <p>Find and organize job opportunities that match your profile:</p>
        <ul style="margin: 8px 0; padding-left: 20px;">
          <li>Search from multiple job boards</li>
          <li>Save interesting positions</li>
          <li>Set up job alerts</li>
          <li>Track company research</li>
        </ul>
        <p><strong>Never miss the perfect opportunity!</strong></p>
      `,
      attachTo: {
        element: '#jobs-nav',
        on: 'bottom'
      },
      buttons: [buttons.back, buttons.skip, buttons.next],
      id: 'jobs-step'
    });

    // Step 4: Resume Polishing
    tour.addStep({
      title: '✨ Step 4: Polish Your Resume',
      text: `
        <p>AI-powered resume optimization for each job application:</p>
        <ul style="margin: 8px 0; padding-left: 20px;">
          <li>Tailor content to job requirements</li>
          <li>Optimize keywords and phrases</li>
          <li>Highlight relevant experiences</li>
          <li>Generate multiple versions</li>
        </ul>
        <p><strong>Stand out from the competition!</strong></p>
      `,
      attachTo: {
        element: '#resumes-nav',
        on: 'bottom'
      },
      buttons: [buttons.back, buttons.skip, buttons.next],
      id: 'polish-step'
    });

    // Step 5: Application Tracking
    tour.addStep({
      title: '📊 Step 5: Track Your Applications',
      text: `
        <div style="text-align: center; margin-bottom: 12px;">
          <div style="font-size: 48px; margin-bottom: 8px;">📋</div>
        </div>
        <p>Stay organized and follow up effectively:</p>
        <ul style="margin: 8px 0; padding-left: 20px;">
          <li>Monitor application status</li>
          <li>Set follow-up reminders</li>
          <li>Track interview dates</li>
          <li>Record feedback and notes</li>
        </ul>
        <p><strong>Never lose track of opportunities!</strong></p>
      `,
      attachTo: {
        element: '#applications-nav',
        on: 'bottom'
      },
      buttons: [buttons.back, buttons.skip, buttons.next],
      id: 'tracking-step'
    });

    // Step 6: Interview Practice
    tour.addStep({
      title: '🎤 Step 6: Practice Interviews',
      text: `
        <p>Prepare for success with AI-powered interview practice:</p>
        <ul style="margin: 8px 0; padding-left: 20px;">
          <li>Job-specific interview questions</li>
          <li>Practice with virtual interviewer</li>
          <li>Get feedback on your answers</li>
          <li>Build confidence before the real thing</li>
        </ul>
        <p><strong>Nail your interviews with confidence!</strong></p>
      `,
      attachTo: {
        element: '#mock-interview-nav',
        on: 'bottom'
      },
      buttons: [buttons.back, buttons.skip, buttons.next],
      id: 'interview-step'
    });

    // Final step
    tour.addStep({
      title: '🚀 Ready to Start Your Journey?',
      text: `
        <div style="text-align: center;">
          <p><strong>You're all set to begin your job search journey!</strong></p>
          <p>Remember: Success comes from following the process step by step.</p>
          <div style="margin: 16px 0; padding: 12px; background: #f0f9ff; border-radius: 8px; border-left: 4px solid #0369a1;">
            <p style="margin: 0; font-weight: 500; color: #0369a1;">💡 Pro Tip</p>
            <p style="margin: 4px 0 0 0; font-size: 14px;">Start with building your profile - everything else flows from there!</p>
          </div>
          <p>Let's get started!</p>
        </div>
      `,
      buttons: [
        buttons.back,
        {
          action() {
            this.complete();
            document.body.classList.remove('shepherd-active');
            // Redirect to profile if user doesn't have one
            if (!userStatus.hasProfile) {
              navigate('/profile');
            }
            return true;
          },
          text: 'Start Building Profile!',
          classes: 'shepherd-button-primary'
        },
        {
          action() {
            this.complete();
            document.body.classList.remove('shepherd-active');
            return true;
          },
          text: 'Finish Tour',
          classes: 'shepherd-button-secondary'
        }
      ],
      id: 'ready-step'
    });

    // Handle tour completion
    tour.on('complete', () => {
      this.markTourCompleted('main_onboarding');
      document.body.classList.remove('shepherd-active');
      this.currentTour = null;
    });

    tour.on('cancel', () => {
      // Mark completed even if skipped, so it only shows once
      this.markTourCompleted('main_onboarding');
      document.body.classList.remove('shepherd-active');
      this.currentTour = null;
    });

    // Handle tour cleanup on hide
    tour.on('hide', () => {
      if (!tour.isActive()) {
        document.body.classList.remove('shepherd-active');
      }
    });

    this.currentTour = tour;
    return tour;
  }

  // Add completion handlers to any tour
  addCompletionHandlers(tour, tourName) {
    tour.on('complete', () => {
      this.markTourCompleted(tourName);
      document.body.classList.remove('shepherd-active');
      this.currentTour = null;
    });

    tour.on('cancel', () => {
      this.markTourCompleted(tourName);
      document.body.classList.remove('shepherd-active');
      this.currentTour = null;
    });

    tour.on('hide', () => {
      if (!tour.isActive()) {
        document.body.classList.remove('shepherd-active');
      }
    });
  }

  // Profile page specific tour
  createProfileTour() {
    const tour = new Shepherd.Tour(this.getDefaultTourOptions());
    const buttons = this.getButtonConfig();

    tour.addStep({
      title: '👤 Professional Profile',
      text: `
        <p>This is where you build your comprehensive professional profile.</p>
        <p>Fill out all sections to get the best career recommendations and job matches.</p>
      `,
      attachTo: {
        element: '.profile-container',
        on: 'top'
      },
      buttons: [buttons.skip, buttons.next],
      id: 'profile-intro'
    });

    // Add more profile-specific steps...

    this.addCompletionHandlers(tour, 'profile');
    this.currentTour = tour;
    return tour;
  }

  // Resume Massage tour
  createResumeMassageTour() {
    const tour = new Shepherd.Tour(this.getDefaultTourOptions());
    const buttons = this.getButtonConfig();

    tour.addStep({
      title: '🧭 Career Direction Finder',
      text: `
        <p>This tool analyzes your resume and suggests the best career paths for you.</p>
        <p>It will help you understand which job roles match your skills and experience.</p>
      `,
      buttons: [buttons.skip, buttons.next],
      id: 'resume-massage-intro'
    });

    tour.addStep({
      title: '📝 Select Your Resume',
      text: `
        <p>Choose the resume version you want to analyze.</p>
        <p>Make sure it's your most up-to-date version for accurate recommendations.</p>
      `,
      attachTo: {
        element: '#resume-select',
        on: 'bottom'
      },
      buttons: [buttons.back, buttons.skip, buttons.next],
      id: 'resume-selection'
    });

    this.addCompletionHandlers(tour, 'resume_massage');
    this.currentTour = tour;
    return tour;
  }

  // Job Application tour
  createJobApplicationTour() {
    const tour = new Shepherd.Tour(this.getDefaultTourOptions());
    const buttons = this.getButtonConfig();

    tour.addStep({
      title: '📋 Job Application Process',
      text: `
        <p>This guided process helps you create the perfect application for this job.</p>
        <p>We'll analyze the job requirements, tailor your resume, and generate a cover letter.</p>
      `,
      buttons: [buttons.skip, buttons.next],
      id: 'job-app-intro'
    });

    this.addCompletionHandlers(tour, 'job_application');
    this.currentTour = tour;
    return tour;
  }

  // Dashboard tour
  createDashboardTour(userStatus) {
    const tour = new Shepherd.Tour(this.getDefaultTourOptions());
    const buttons = this.getButtonConfig();

    tour.addStep({
      title: '🏠 Your Dashboard',
      text: `
        <p>Welcome to your personal job search command center!</p>
        <p>Here you can see your progress and quick actions to move forward.</p>
      `,
      buttons: [buttons.skip, buttons.next],
      id: 'dashboard-intro'
    });

    // Add step for quick actions
    tour.addStep({
      title: '⚡ Smart Quick Actions',
      text: `
        <p>These smart actions guide you to your next step based on your progress:</p>
        <ul style="margin: 8px 0; padding-left: 20px;">
          <li>Upload resume if you don't have one</li>
          <li>Build profile if incomplete</li>
          <li>Find jobs when you're ready</li>
          <li>Practice interviews for applications</li>
        </ul>
      `,
      attachTo: {
        element: '.quick-actions',
        on: 'top'
      },
      buttons: [buttons.back, buttons.skip, buttons.next],
      id: 'quick-actions'
    });

    this.addCompletionHandlers(tour, 'dashboard');
    this.currentTour = tour;
    return tour;
  }

  // Start a tour
  startTour(tourName, ...args) {
    // Support optional options object as first arg: { force: boolean }
    let options = {};
    let ctorArgs = args;
    if (args.length > 0 && typeof args[0] === 'object' && args[0] !== null && Object.prototype.hasOwnProperty.call(args[0], 'force')) {
      options = args[0] || {};
      ctorArgs = args.slice(1);
    }

    // Respect completion unless force requested
    if (!options.force && this.isTourCompleted(tourName)) {
      return;
    }

    if (this.currentTour) {
      this.currentTour.cancel();
    }

    let tour = null;
    switch (tourName) {
      case 'main_onboarding':
        tour = this.createMainOnboardingTour(...ctorArgs);
        break;
      case 'profile':
        tour = this.createProfileTour(...ctorArgs);
        break;
      case 'resume_massage':
        tour = this.createResumeMassageTour(...ctorArgs);
        break;
      case 'job_application':
        tour = this.createJobApplicationTour(...ctorArgs);
        break;
      case 'dashboard':
        tour = this.createDashboardTour(...ctorArgs);
        break;
      default:
        console.warn(`Unknown tour: ${tourName}`);
        return;
    }

    if (tour) {
      tour.start();
    }
  }

  // Cancel current tour
  cancelTour() {
    if (this.currentTour) {
      this.currentTour.cancel();
      this.currentTour = null;
    }
    // Force cleanup
    document.body.classList.remove('shepherd-active');
  }

  // Emergency cleanup - force remove all tour elements
  forceCleanup() {
    // Cancel any active tour
    this.cancelTour();
    
    // Remove all shepherd elements from DOM
    const shepherdElements = document.querySelectorAll('.shepherd-element');
    shepherdElements.forEach(el => el.remove());
    
    // Remove active class
    document.body.classList.remove('shepherd-active');
    
    // Clear current tour
    this.currentTour = null;
  }

  // Reset a single tour's completion state
  resetTour(tourName) {
    if (this.tourCompletionStatus[tourName]) {
      delete this.tourCompletionStatus[tourName];
      this.saveTourStatus();
    }
  }
}

// Create singleton instance
const tourService = new TourService();

export default tourService;